import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";
import { importIdentity } from "@/pages/ImportHistory";

const canScan = (role) => role === "CHECKER_OPERATOR" || role === "OWNER";

export default function Checker() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [imports, setImports] = useState([]);
  const [selectedImport, setSelectedImport] = useState("");
  const [batch, setBatch] = useState(null);
  const [awb, setAwb] = useState("");
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const scanRef = useRef(null);
  const awbRef = useRef(null);
  const resetTimer = useRef(null);
  const selectedDaRef = useRef(null);
  const selectedTargetBatchId = batches
    .filter((b) => b.import_batch_id === selectedImport && b.status === "OPEN")
    .sort((a, b) => Number(b.sequence_no || 0) - Number(a.sequence_no || 0))[0]?.id
    || batches.find((b) => b.import_batch_id === selectedImport)?.id
    || null;

  const loadBatches = () => api.get("/checker/batches").then(({ data }) => setBatches(data)).catch(() => {});
  const loadImports = () => api.get("/checker/importable-batches").then(({ data }) => setImports(data)).catch(() => {});
  useEffect(() => { loadBatches(); loadImports(); }, []);
  useEffect(() => { if (session && scanRef.current) scanRef.current.focus(); }, [session]);
  // Hands-free operator flow: after an order is completed, return to AWB scan.
  useEffect(() => { if (batch && !session && awbRef.current) awbRef.current.focus(); }, [session, batch]);
  useEffect(() => () => { if (resetTimer.current) clearTimeout(resetTimer.current); }, []);
  useEffect(() => {
    if (!selectedImport || batch || !selectedDaRef.current) return;
    selectedDaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedImport, batch, selectedTargetBatchId]);

  const createBatch = async () => {
    setError("");
    if (!selectedImport) { setError("Pilih Import Batch dulu untuk membuat DA."); return; }
    try {
      const { data } = await api.post("/checker/batches", { import_batch_id: selectedImport });
      await loadBatches(); setBatch(data);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const refreshBatch = async () => { if (batch) { const { data } = await api.get(`/checker/batches/${batch.id}`); setBatch(data); } };

  const closeBatch = async () => {
    setError("");
    try { const { data } = await api.post(`/checker/batches/${batch.id}/close`); setBatch(data); await loadBatches(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const openAwb = async (e) => {
    e.preventDefault(); setError(""); setFeedback(null); setSession(null);
    try {
      const { data } = await api.post(`/checker/batches/${batch.id}/scan-awb`, { awb: awb.trim() });
      setSession(data); setAwb(""); await refreshBatch();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); await refreshBatch(); }
  };

  const doScan = async (e) => {
    e.preventDefault();
    const code = scanCode.trim(); if (!code || !session || scanBusy) return; setScanBusy(true); setScanCode("");
    try {
      const { data } = await api.post(`/checker/sessions/${session.session_id}/scan`, { scan_code: code });
      setSession(data);
      const t = data.last_event?.event_type;
      if (t === "VALID_SCAN") {
        const it = data.progress.find((p) => p.sku_code === data.last_event.sku_code);
        setFeedback({ type: "ok", text: `✓ OK — ${it ? `${it.scanned_qty}/${it.required_qty}` : ""} ${data.last_event.sku_code}` });
      } else if (t === "WRONG_SKU") setFeedback({ type: "bad", text: "❌ SALAH BARANG — TAMPOL NIH!" });
      else if (t === "OVER_SCAN") setFeedback({ type: "warn", text: "⚠ QTY SUDAH CUKUP" });
      else setFeedback({ type: "bad", text: "❌ KODE TIDAK DIKENAL" });
      if (data.status === "SCAN_VALID") {
        setFeedback({ type: "ok", text: "✅ ORDER SELESAI — scan AWB berikutnya" });
        refreshBatch();
        resetTimer.current = setTimeout(() => { setSession(null); setFeedback(null); }, 1200);
      }
    } catch (err) { setFeedback({ type: "bad", text: formatApiErrorDetail(err.response?.data?.detail) || err.message }); }
    finally { setScanBusy(false); }
    if (scanRef.current) scanRef.current.focus();
  };

  if (user && !canScan(user.role)) {
    return (<div className="app-shell"><AppTopBar active="checker" /><main className="content checker-content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="checker" />
      <main className="content checker-content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        {/* Preview mirrors Production: one create control above the DA history list. */}
        {!batch && (
          <section className="panel" data-testid="batch-select-panel">
            <div className="panel-head">
              <h2 className="panel-title">Buat DA dari Import</h2>
            </div>
            <div className="form-row" style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <label className="field-label">Import Batch (Work Source)</label>
                <select className="field-input" value={selectedImport} onChange={(e) => setSelectedImport(e.target.value)} data-testid="import-batch-select">
                  <option value="">-- Pilih Import Batch --</option>
                  {imports.map((im) => (
                    <option key={im.id} value={im.id} data-testid={`import-option-${im.id}`}>
                      {importIdentity(im.imported_at, im.imported_by_name)} · {im.total_orders} order · {im.original_filename || "nama file tidak tersedia"}
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn-primary inline" onClick={createBatch} data-testid="create-batch-button">+ Buat DA Baru</button>
            </div>
            <div className="table-scroll checker-history-scroll" style={{ marginTop: 16 }} data-testid="batch-history-scroll">
              <table className="data-table" data-testid="batch-table">
                <thead><tr><th>DA KE</th><th>IMPORT</th><th>WORK DATE</th><th>TOTAL</th><th>SELESAI</th><th>BELUM</th><th>STATUS</th><th>AKSI</th></tr></thead>
                <tbody>
                  {batches.length === 0 && <tr><td colSpan={8} className="empty-cell">Belum ada DA. Buat DA baru dari Import.</td></tr>}
                  {batches.map((b) => (
                    <tr key={b.id} data-testid={`batch-row-${b.batch_code}-${b.work_date}`}>
                      <td className="mono">DA KE {b.sequence_no}</td>
                      <td className="checker-import-cell">
                        {(() => {
                          const im = imports.find((item) => String(item.id) === String(b.import_batch_id));
                          return (
                            <>
                              <div className="checker-import-name">
                                {im?.original_filename || b.import_filename || "Import Batch tidak ditemukan"}
                              </div>
                            </>
                          );
                        })()}
                      </td>
                      <td>{b.work_date}</td><td>{b.total}</td><td className="done">{b.selesai}</td><td>{b.belum_selesai}</td>
                      <td><span className={`status-pill ${b.status === "OPEN" ? "on" : "off"}`}>{b.status}</span></td>
                      <td><button className="btn-mini" onClick={() => setBatch(b)} data-testid={`open-batch-${b.batch_code}-${b.work_date}`}>Buka</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {batch && (
          <section className="panel" data-testid="active-batch-panel">
            <div className="panel-head">
              <h2 className="panel-title" data-testid="active-batch-label">DA KE {batch.sequence_no}</h2>
              <span className={`status-pill ${batch.status === "OPEN" ? "on" : "off"}`} data-testid="batch-status">{batch.status}</span>
            </div>
            <div className="meta-grid">
              <div><span className="meta-label">Import</span><span data-testid="batch-import">{batch.import_filename || "-"}</span></div>
              <div><span className="meta-label">Work Date</span><span>{batch.work_date}</span></div>
              <div><span className="meta-label">Valid Scan</span><span data-testid="batch-valid-scan">{batch.valid_scan}</span></div>
              <div><span className="meta-label">Rejected</span><span data-testid="batch-rejected">{batch.rejected_scan}</span></div>
              <div><span className="meta-label">Blocked</span><span data-testid="batch-blocked">{batch.blocked_attempt}</span></div>
            </div>
            <div className="form-actions">
              <button className="btn-ghost" onClick={() => { setBatch(null); setSession(null); loadBatches(); }} data-testid="change-batch">Ganti DA</button>
              {batch.status === "OPEN" && <button className="btn-ghost" onClick={closeBatch} data-testid="close-batch-button">Tutup DA</button>}
            </div>
          </section>
        )}

        {batch && batch.status === "OPEN" && !session && (
          <section className="panel" data-testid="scan-resi-panel">
            <h2 className="panel-title">Scan Resi</h2>
            <form onSubmit={openAwb}>
              <label className="field-label">Scan / masukkan AWB</label>
              <input ref={awbRef} className="field-input" autoFocus value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="AWB / No. Resi" data-testid="awb-input" />
              <div className="form-actions"><button className="btn-primary" type="submit" data-testid="open-awb-button">Buka Order</button></div>
            </form>
          </section>
        )}

        {session && (
          <>
            <section className={`panel scan-status ${session.status === "SCAN_VALID" ? "valid" : ""}`} data-testid="scan-session-panel">
              <div className="panel-head">
                <h2 className="panel-title">Order {session.source_order_id}</h2>
                <span className={`status-pill ${session.status === "SCAN_VALID" ? "on" : "off"}`} data-testid="session-status">{session.status}</span>
              </div>
              <div className="meta-grid"><div><span className="meta-label">AWB</span><span className="mono">{session.tracking_no}</span></div></div>
              {feedback && <div className={`scan-feedback ${feedback.type}`} data-testid="scan-feedback">{feedback.text}</div>}
              <form onSubmit={doScan} style={{ marginTop: 14 }}>
                <label className="field-label">Scan barcode barang</label>
                <input ref={scanRef} className="field-input" value={scanCode} onChange={(e) => setScanCode(e.target.value)} placeholder="Scan kode barang..." data-testid="scan-code-input" disabled={session.status === "SCAN_VALID"} />
              </form>
            </section>
            <section className="panel" data-testid="progress-panel">
              <h2 className="panel-title">Progress Scan</h2>
              <div className="table-scroll">
                <table className="data-table" data-testid="progress-table">
                  <thead><tr><th>SKU</th><th>Progress</th></tr></thead>
                  <tbody>
                    {session.progress.map((p) => (
                      <tr key={p.product_id} data-testid={`progress-row-${p.sku_code}`}>
                        <td className="mono">{p.sku_code}</td>
                        <td className={p.scanned_qty >= p.required_qty ? "mono done" : "mono"} data-testid={`progress-${p.sku_code}`}>{p.scanned_qty} / {p.required_qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-actions"><button className="btn-ghost" onClick={() => { setSession(null); setFeedback(null); refreshBatch(); }} data-testid="scan-next-order">Order Berikutnya</button></div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
